import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text,
  Toast,
  Toaster,
  ToastTitle,
  tokens,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  ArrowReset20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useNavigate, useSearchParams } from "react-router-dom";

import { saveCorrections } from "@/api/documents";
import { ApprovalBadge } from "@/components/ApprovalBadge";
import { ApprovalActions } from "@/features/review/ApprovalActions";
import { DocumentTradeKey } from "@/features/review/DocumentTradeKey";
import { DocumentTypeLabel } from "@/features/review/DocumentTypeLabel";
import { DocumentViewer } from "@/features/review/DocumentViewer";
import { ErrorDetailsPanel } from "@/features/review/ErrorDetailsPanel";
import { ExtractedFieldsForm } from "@/features/review/ExtractedFieldsForm";
import { ParcelSelector } from "@/features/review/ParcelSelector";
import { ParcelSummaryRow } from "@/features/review/ParcelSummaryRow";
import { SymphonyUpdatePanel } from "@/features/review/SymphonyUpdatePanel";
import {
  fromFormValues,
  toFormDefaults,
  type FieldValues,
} from "@/features/review/fieldFormSchema";
import { useDocumentTypes } from "@/features/documents/useDocumentTypes";
import { isArchiveOnly as checkArchiveOnly, type DocumentDetail } from "@/types/domain";

const TOPBAR_H = 60;
const ACTIONBAR_H = 68;

export function ReviewLayout({ detail }: { detail: DocumentDetail }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toasterId = useId("review-toaster");

  const goBack = () => {
    // Preserve the filter params we travelled in with; restore the
    // open email drawer if there was one.
    const params = new URLSearchParams(searchParams);
    const fromEmail = params.get("fromEmail");
    params.delete("fromEmail");
    if (fromEmail) params.set("email", fromEmail);
    const qs = params.toString();
    navigate(qs ? `/?${qs}` : "/");
  };
  const { item, pages, extractedFields, textContent } = detail;
  const { data: documentTypes } = useDocumentTypes();
  const archiveOnly = checkArchiveOnly(item.documentType, documentTypes);
  const isApproved = item.approvalStatus === "approved";
  const hasFailed =
    item.status === "classification_failed" ||
    item.status === "extraction_failed";

  const [parcelSelectorOpen, setParcelSelectorOpen] = useState(false);

  const qc = useQueryClient();
  const { dispatchToast } = useToastController(toasterId);

  const { control, formState, getValues, reset } = useForm<FieldValues>({
    defaultValues: toFormDefaults(extractedFields),
    values: toFormDefaults(extractedFields),
    resetOptions: { keepDirtyValues: true },
  });

  const saveMutation = useMutation({
    mutationFn: (correctedData: Record<string, unknown> | null) =>
      saveCorrections(item.id, correctedData),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document", item.id] });
      void qc.invalidateQueries({ queryKey: ["email", item.emailMessageIdHash] });
      dispatchToast(
        <Toast>
          <ToastTitle>Corrections saved</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
    },
    onError: (err) => {
      dispatchToast(
        <Toast>
          <ToastTitle>
            {err instanceof Error ? err.message : "Save failed"}
          </ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    },
  });

  const fieldsEditable = !isApproved && !archiveOnly;
  const isDirty = formState.isDirty;

  const handleSave = () => {
    const { values, errors } = fromFormValues(extractedFields, getValues());
    if (Object.keys(errors).length > 0) {
      const first = Object.entries(errors)[0];
      dispatchToast(
        <Toast>
          <ToastTitle>
            {first ? `${first[0]}: ${first[1]}` : "Invalid input"}
          </ToastTitle>
        </Toast>,
        { intent: "error" },
      );
      return;
    }
    saveMutation.mutate(values);
  };

  const handleReset = () => {
    reset(toFormDefaults(extractedFields));
    saveMutation.mutate(null);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: tokens.colorNeutralBackground2,
      }}
    >
      {/* Top bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: TOPBAR_H,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 16px",
          borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
          background: tokens.colorNeutralBackground1,
          boxSizing: "border-box",
        }}
      >
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={goBack}
        >
          Back
        </Button>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Text
            weight="semibold"
            size={400}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <DocumentTypeLabel typeKey={item.documentType} />
            <span
              style={{
                color: tokens.colorNeutralForeground2,
                fontWeight: "normal",
                marginLeft: 8,
              }}
            >
              · {item.attachmentName ?? "Email body"}
            </span>
          </Text>
          <button
            type="button"
            onClick={() => {
              if (item.emailMessageIdHash) {
                const params = new URLSearchParams(searchParams);
                params.delete("fromEmail");
                params.set("email", item.emailMessageIdHash);
                navigate(`/?${params.toString()}`);
              }
            }}
            disabled={!item.emailMessageIdHash}
            title={
              item.emailMessageIdHash
                ? "Open email details"
                : item.emailSubject || ""
            }
            style={{
              all: "unset",
              cursor: item.emailMessageIdHash ? "pointer" : "default",
              padding: "2px 4px",
              borderRadius: 4,
              maxWidth: "100%",
              minWidth: 0,
            }}
            onMouseEnter={(e) => {
              if (item.emailMessageIdHash) {
                e.currentTarget.style.background = tokens.colorNeutralBackground1Hover;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <Text
              size={200}
              style={{
                color: tokens.colorNeutralForeground2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecoration: item.emailMessageIdHash ? "underline dotted" : "none",
                textUnderlineOffset: 2,
              }}
              block
            >
              {item.emailSubject || "(no subject)"}
              {item.emailFrom ? ` · ${item.emailFrom}` : ""}
            </Text>
          </button>
        </div>
        <div style={{ flexShrink: 0 }}>
          <ApprovalBadge
            status={item.status}
            approvalStatus={item.approvalStatus}
            archiveOnly={archiveOnly}
          />
        </div>
      </div>

      {/* Middle */}
      <div
        style={{
          position: "absolute",
          top: TOPBAR_H,
          bottom: ACTIONBAR_H,
          left: 0,
          right: 0,
        }}
      >
        <PanelGroup
          autoSaveId="review-split"
          direction="horizontal"
          style={{ height: "100%" }}
        >
          <Panel defaultSize={45} minSize={20} collapsible>
            <div
              style={{
                height: "100%",
                overflowX: "hidden",
                overflowY: "auto",
                padding: 16,
                background: tokens.colorNeutralBackground1,
                boxSizing: "border-box",
              }}
            >
              <DocumentTradeKey
                documentId={item.id}
                tradeKey={item.tradeKey}
                tradeKeyOverride={item.tradeKeyOverride}
                emailTradeKey={item.emailTradeKey}
                folderUrl={item.documentFolderUrl}
                readOnly={isApproved}
                footer={
                  archiveOnly ? null : (
                    <ParcelSummaryRow
                      emailId={item.emailMessageIdHash}
                      documentId={item.id}
                      selectedParcelNumber={detail.selectedParcelNumber}
                      candidateCount={detail.parcelCandidates.length}
                      disabled={isApproved}
                      onChange={() => setParcelSelectorOpen(true)}
                    />
                  )
                }
              />
              {archiveOnly ? (
                <MessageBar intent="info">
                  <MessageBarBody>
                    <MessageBarTitle>Archive only</MessageBarTitle>
                    This document is archived for reference only. No data
                    extraction is performed.
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <>
                  {/* Keep the Symphony panel mounted post-approval so it
                      renders the pushed payload in its own read-only mode
                      (driven by ``detail.symphonyPayloadData``). The panel
                      shows its own "Pushed to Symphony" success banner with
                      timestamp, so no extra banner is needed here. */}
                  <SymphonyUpdatePanel detail={detail} toasterId={toasterId} />
                  {hasFailed ? (
                    <ErrorDetailsPanel
                      errorLog={detail.errorLog}
                      pipelineErrors={detail.pipelineErrors}
                    />
                  ) : null}
                  <div
                    style={{
                      background: tokens.colorNeutralBackground1,
                      padding: 12,
                      border: `1px solid ${tokens.colorNeutralStroke2}`,
                      borderRadius: 6,
                      marginBottom: 12,
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <Text size={300} weight="semibold">
                      Extracted data
                    </Text>
                    <ExtractedFieldsForm
                      control={control}
                      initial={extractedFields}
                      readOnly={!fieldsEditable}
                    />
                    {fieldsEditable ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 8,
                        }}
                      >
                        <Button
                          icon={<ArrowReset20Regular />}
                          onClick={handleReset}
                          disabled={saveMutation.isPending}
                          title="Discard saved corrections and revert to extracted values"
                        >
                          Reset to extracted
                        </Button>
                        <Button
                          appearance="primary"
                          icon={<Save20Regular />}
                          onClick={handleSave}
                          disabled={!isDirty || saveMutation.isPending}
                        >
                          {saveMutation.isPending
                            ? "Saving…"
                            : "Save corrections"}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </Panel>
          <PanelResizeHandle
            style={{
              width: 4,
              background: tokens.colorNeutralStroke2,
              cursor: "col-resize",
            }}
          />
          <Panel defaultSize={55} minSize={30}>
            <div style={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
              <DocumentViewer
                documentId={item.id}
                pages={pages}
                textContent={textContent}
              />
            </div>
          </Panel>
        </PanelGroup>
      </div>

      {/* Action bar */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: ACTIONBAR_H,
          boxSizing: "border-box",
        }}
      >
        <ApprovalActions
          documentId={item.id}
          approvalStatus={item.approvalStatus}
          toasterId={toasterId}
          archiveOnly={archiveOnly}
        />
      </div>

      {parcelSelectorOpen ? (
        <ParcelSelector
          emailId={item.emailMessageIdHash}
          documentId={item.id}
          selectedParcelNumber={detail.selectedParcelNumber}
          parcelCandidates={detail.parcelCandidates}
          parcelFetchStatus={detail.parcelFetchStatus}
          open={parcelSelectorOpen}
          onClose={() => setParcelSelectorOpen(false)}
          toasterId={toasterId}
        />
      ) : null}
      <Toaster toasterId={toasterId} position="top-end" />
    </div>
  );
}

